import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiError, brl } from "@/lib/api";
import { toast } from "sonner";
import { X, MapPin, Truck, ClipboardList } from "lucide-react";
import useInfiniteList from "@/hooks/useInfiniteList";
import { ConfirmDialog, StatusBadge } from "@/components/panel/ui";
import { PageHeader, SearchInput } from "@/components/ui-kit";

const COLUMNS = [
  ["pendente", "Pendente", "border-slate-300"],
  ["pago", "Pago", "border-blue-400"],
  ["separacao", "Em separação", "border-amber-400"],
  ["enviado", "Enviado", "border-violet-400"],
  ["entregue", "Entregue", "border-emerald-400"],
  ["cancelado", "Cancelado", "border-red-400"],
];
const NEXT = { pendente: "pago", pago: "separacao", separacao: "enviado", enviado: "entregue" };
const NEXT_LABEL = { pendente: "Confirmar pagamento", pago: "Iniciar separação", separacao: "Marcar enviado", enviado: "Marcar entregue" };
const PERIODS = [["7", "7 dias"], ["30", "30 dias"], ["90", "90 dias"], ["", "Tudo"]];

function OrderDrawer({ order, onClose, onMove, onCancel }) {
  const a = order?.address || {};
  return (
    <Dialog.Root open={!!order} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40" />
        <Dialog.Content data-testid="order-drawer"
          className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-white border-l border-slate-200 flex flex-col focus:outline-none">
          {order && <>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <Dialog.Title className="font-mono font-semibold text-lg text-slate-900">{order.number}</Dialog.Title>
            <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString("pt-BR")}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge value={order.status} />
            <Dialog.Close data-testid="order-drawer-close" className="p-1 text-slate-400 hover:text-slate-700" aria-label="Fechar"><X size={19} /></Dialog.Close>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-5 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Cliente</p>
            <p className="font-medium text-slate-900">{order.customer_name}</p>
            <p className="text-slate-500">{order.customer_email}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Itens ({order.items?.length || 0})</p>
            <div className="space-y-2">
              {(order.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between gap-3 border border-slate-100 rounded-lg px-3 py-2">
                  <span className="text-slate-700">{i.qty}x {i.product_name} <span className="text-slate-400">({i.variant_label})</span></span>
                  <span className="font-mono shrink-0">{brl(i.total)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{brl(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Cupom {order.coupon_code}</span><span className="font-mono">-{brl(order.discount)}</span></div>}
            {order.pix_discount > 0 && <div className="flex justify-between text-emerald-600"><span>Desconto PIX</span><span className="font-mono">-{brl(order.pix_discount)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>Frete ({order.shipping_method})</span><span className="font-mono">{order.shipping_cost === 0 ? "Grátis" : brl(order.shipping_cost)}</span></div>
            <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-100"><span>Total ({order.payment_method})</span><span className="font-mono">{brl(order.total)}</span></div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1"><MapPin size={12} /> Endereço de entrega</p>
            <p className="text-slate-700">{a.street}, {a.number}{a.complement ? ` — ${a.complement}` : ""}</p>
            <p className="text-slate-500">{a.district} · {a.city}/{a.state} · CEP {a.cep}</p>
          </div>

          {order.tracking_code && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1"><Truck size={12} /> Rastreio</p>
              <p className="font-mono text-brand-700">{order.tracking_code}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 space-y-2">
          {/* Mover para qualquer status por teclado/toque (alternativa acessível ao arrastar). */}
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Mover para</span>
            <select
              value={order.status}
              onChange={(e) => { const v = e.target.value; if (v === order.status) return; v === "cancelado" ? onCancel(order) : onMove(order, v); }}
              data-testid="drawer-status-select"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100">
              {COLUMNS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {NEXT[order.status] && (
            <button onClick={() => onMove(order, NEXT[order.status])} data-testid="drawer-advance-btn"
              className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold py-2.5 rounded-lg">
              {NEXT_LABEL[order.status]} →
            </button>
          )}
          {order.status !== "cancelado" && order.status !== "entregue" && (
            <button onClick={() => onCancel(order)} data-testid="drawer-cancel-btn"
              className="w-full text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 py-2.5 rounded-lg">
              Cancelar pedido
            </button>
          )}
        </div>
          </>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Uma coluna do quadro = uma consulta paginada própria, filtrada por status. */
function OrderColumn({ colKey, label, border, search, from, dragOver, onDragOver, onDragLeave, onDrop, onDragStart, onSelect, onMove, onCancel }) {
  const { items, total, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteList("/admin/orders", {
      status: colKey,
      search: search || undefined,
      from: from || undefined,
    }, { perPage: 15 });

  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      className={`bg-white border rounded-lg border-t-4 ${border} transition-colors ${dragOver ? "border-brand-400 bg-brand-50/40" : "border-slate-200"}`}
      data-testid={`kanban-col-${colKey}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
        <span className="font-semibold text-sm text-slate-900">{label}</span>
        <span className="text-xs font-mono bg-slate-100 rounded-full px-2 py-0.5">{total}</span>
      </div>
      <div className="p-3 space-y-3 min-h-[90px] max-h-[60vh] overflow-y-auto scrollbar-thin">
        {items.map((o) => (
          <div key={o.id} draggable={o.status !== "cancelado"}
            onDragStart={() => onDragStart(o)}
            onClick={() => onSelect(o)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(o); } }}
            role="button" tabIndex={0} aria-label={`Pedido ${o.number}, ${o.customer_name}. Abrir detalhes`}
            className="border border-slate-200 rounded-lg p-3 text-sm cursor-pointer hover:border-brand-400 hover:shadow-sm active:cursor-grabbing transition-all focus:outline-none focus:ring-2 focus:ring-brand-400"
            data-testid="kanban-order-card">
            <div className="flex justify-between">
              <span className="font-mono font-semibold text-slate-900">{o.number}</span>
              <span className="font-mono text-slate-600">{brl(o.total)}</span>
            </div>
            <p className="text-slate-500 text-xs mt-1">{o.customer_name} · {(o.items || []).length} item(ns)</p>
            {o.tracking_code && <p className="text-xs font-mono text-brand-700 mt-1">{o.tracking_code}</p>}
            {/* `h-9`/`h-8` no lugar de `py-1.5`/`py-1`, que davam 28px e 24px.
                Arrastar o cartão não existe no celular: estes dois botões SÃO a
                forma de mover e cancelar ali, e eram os menores da tela. */}
            {NEXT[o.status] && (
              <button onClick={(e) => { e.stopPropagation(); onMove(o, NEXT[o.status]); }} data-testid="kanban-advance-btn"
                className="mt-2 w-full h-9 text-xs font-semibold bg-slate-900 hover:bg-slate-700 text-white rounded-md transition-colors">
                {NEXT_LABEL[o.status]} →
              </button>
            )}
            {o.status !== "cancelado" && o.status !== "entregue" && (
              <button onClick={(e) => { e.stopPropagation(); onCancel(o); }} data-testid="kanban-cancel-btn"
                className="mt-1 w-full h-8 text-xs text-red-500 hover:bg-red-50 rounded-md">Cancelar</button>
            )}
          </div>
        ))}
        {isLoading && <p className="text-xs text-slate-400 px-1 py-2">Carregando...</p>}
        {isError && <p className="text-xs text-red-500 px-1 py-2">Erro ao carregar esta coluna.</p>}
        {/* Arrastar não existe no toque: no celular a instrução era uma ordem
            impossível repetida em seis colunas vazias. Lá o botão do cartão é
            que move o pedido — e é o que a descrição da tela explica. */}
        {!isLoading && !isError && items.length === 0 && (
          <p className="text-xs text-slate-300 px-1 py-2">
            <span className="hidden md:inline">Arraste um pedido para cá</span>
            <span className="md:hidden">Nenhum pedido nesta etapa</span>
          </p>
        )}
        {hasNextPage && (
          <button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
            data-testid={`kanban-more-${colKey}`}
            className="w-full text-xs font-semibold text-slate-500 hover:text-brand-600 border border-slate-200 rounded-md py-1.5 transition-colors">
            {isFetchingNextPage ? "Carregando..." : `Carregar mais (${items.length}/${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminOrders() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [period, setPeriod] = useState("30");
  const [selected, setSelected] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);

  // Debounce: são 6 colunas, uma requisição por tecla sairia caro.
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Período vira data e é recortado no banco (antes era filtrado no navegador).
  const from = period
    ? new Date(Date.now() - parseInt(period, 10) * 86400000).toISOString().slice(0, 10)
    : "";

  // O pedido troca de coluna: recarrega o quadro inteiro.
  const reload = () => queryClient.invalidateQueries({ queryKey: ["/admin/orders"] });

  const move = async (order, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/status`, { status });
      setSelected((s) => (s?.id === order.id ? data : s));
      reload();
      toast.success(status === "cancelado"
        ? `Pedido ${order.number} cancelado — estoque devolvido`
        : `Pedido ${order.number} → ${status}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = (colKey) => (e) => {
    e.preventDefault();
    setDragOver(null);
    const order = dragging;
    setDragging(null);
    if (!order || order.status === colKey || order.status === "cancelado") return;
    if (colKey === "cancelado") { setCancelling(order); return; }
    move(order, colKey);
  };

  return (
    <div className="space-y-5" data-testid="admin-orders-page">
      <PageHeader
        eyebrow="Operação"
        title="Pedidos"
        icon={ClipboardList}
        description="Pedidos da loja online, do recebimento à entrega. Use o botão do cartão para avançar a etapa (no computador dá para arrastar) — cancelar devolve o estoque."
        actions={(
          // UM filho só, que cuida do próprio empilhamento. O PageHeader dá
          // `flex-1` a cada filho direto das ações: com busca e período soltos,
          // os dois dividiam UMA linha de 358px — a busca virava "Nº do pedic"
          // e cada "30 dias" quebrava em duas linhas. Empilhar por fora não
          // funcionava (o `flex-1` do pai vence o `basis-full` do filho).
          <div className="w-full flex flex-col md:flex-row md:items-center gap-2">
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nº do pedido ou cliente"
              data-testid="orders-search-input" aria-label="Buscar pedido por número ou cliente"
              width="w-full md:w-52" />
            <div className="flex bg-white border border-slate-200 rounded-xl p-1" role="group" aria-label="Filtrar por período">
              {PERIODS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setPeriod(v)} data-testid={`orders-period-${v || "all"}`}
                  aria-pressed={period === v}
                  className={`flex-1 md:flex-none whitespace-nowrap text-xs font-semibold px-3 h-9 rounded-md transition-colors ${period === v ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 items-start">
        {COLUMNS.map(([key, label, border]) => (
          <OrderColumn key={key} colKey={key} label={label} border={border}
            search={term} from={from}
            dragOver={dragOver === key}
            onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
            onDragLeave={() => setDragOver((d) => (d === key ? null : d))}
            onDrop={onDrop(key)}
            onDragStart={setDragging}
            onSelect={setSelected} onMove={move} onCancel={setCancelling} />
        ))}
      </div>

      <OrderDrawer order={selected} onClose={() => setSelected(null)} onMove={move} onCancel={setCancelling} />

      <ConfirmDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={() => { move(cancelling, "cancelado"); setSelected(null); }}
        title="Cancelar pedido"
        message={cancelling ? `Cancelar o pedido ${cancelling.number} de ${cancelling.customer_name} (${brl(cancelling.total)})? O estoque dos itens será devolvido automaticamente. Esta ação não pode ser desfeita.` : ""}
        confirmLabel="Cancelar pedido"
      />
    </div>
  );
}
